import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import type { Category, EventId, PlanEvent } from "../../../shared/types";
import { useCategories } from "../../../shared/stores/category-store";
import type { TemplateSlot } from "../utils/derive-template";
import { DAY_LABELS } from "../utils/derive-template";
import { ConfirmDialog } from "../../../ui/atoms/ConfirmDialog";
import { CustomSelect } from "../../../ui/atoms/CustomSelect";
import { TimePicker } from "../../../ui/atoms/TimePicker";

const DAY_SHORT_LABELS = ["D", "L", "M", "M", "J", "V", "S"] as const;

interface ScheduleSlotEditorProps {
  readonly slot: TemplateSlot;
  readonly event: PlanEvent;
  readonly onSave: (
    id: EventId,
    updates: Partial<
      Pick<
        PlanEvent,
        "title" | "startTime" | "endTime" | "category" | "description" | "tags"
      >
    >,
  ) => void;
  readonly onDelete: (id: EventId) => void;
  readonly onClose: () => void;
}

export function ScheduleSlotEditor({
  slot,
  event,
  onSave,
  onDelete,
  onClose,
}: ScheduleSlotEditorProps) {
  const { categories, labels } = useCategories();
  const [title, setTitle] = useState(slot.title);
  const [startTime, setStartTime] = useState(slot.startTime);
  const [endTime, setEndTime] = useState(slot.endTime);
  const [category, setCategory] = useState<Category>(slot.category);
  const [description, setDescription] = useState(slot.description);
  const [tagsInput, setTagsInput] = useState(slot.tags.join(", "));
  const [confirmDelete, setConfirmDelete] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Get days of week for weekly recurrence
  const daysOfWeek = event.recurrence?.type === "weekly" ? event.recurrence.daysOfWeek : null;

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    },
    [onClose],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  const handleSave = () => {
    const tags = tagsInput
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
    onSave(slot.eventId, {
      title: title.trim() || slot.title,
      startTime,
      endTime,
      category,
      description,
      tags,
    });
    onClose();
  };

  const handleDelete = () => {
    onDelete(slot.eventId);
    setConfirmDelete(false);
    onClose();
  };

  const recurrenceLabel =
    slot.recurrenceType === "daily"
      ? "Tous les jours"
      : slot.recurrenceType === "monthly"
        ? "Mensuel"
        : daysOfWeek && daysOfWeek.length > 1
          ? daysOfWeek.map((d) => DAY_SHORT_LABELS[d]).join(" ")
          : DAY_LABELS[slot.dayOfWeek] ?? "";

  const inputCls =
    "w-full px-3 py-2 text-xs bg-surface border border-border/60 rounded-md text-ink focus:outline-none focus:border-accent/60";

  return createPortal(
    <>
      <div
        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto"
        onClick={onClose}
        role="presentation"
      >
        <div
          ref={dialogRef}
          data-ph-no-capture
          className="bg-surface border border-border/60 rounded-2xl shadow-xl w-[420px] max-w-[92vw] p-5 space-y-4 my-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold text-ink">
                Modifier le creneau
              </h2>
              <span className="text-[10px] text-ink-faint">{recurrenceLabel}</span>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="w-7 h-7 flex items-center justify-center rounded-full text-ink-muted hover:text-ink hover:bg-surface-alt text-sm"
            >
              ×
            </button>
          </div>

          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Titre"
            autoFocus
            className={inputCls}
          />

          <div className="flex gap-2">
            <div className="flex-1">
              <label className="text-[10px] text-ink-faint uppercase tracking-wide mb-1.5 block">
                Début
              </label>
              <TimePicker value={startTime} onChange={setStartTime} />
            </div>
            <div className="flex-1">
              <label className="text-[10px] text-ink-faint uppercase tracking-wide mb-1.5 block">
                Fin
              </label>
              <TimePicker value={endTime} onChange={setEndTime} />
            </div>
          </div>

          <div>
            <label className="text-[10px] text-ink-faint uppercase tracking-wide mb-1.5 block">
              Catégorie
            </label>
            <CustomSelect
              value={category}
              onChange={(val) => setCategory(val as Category)}
              options={categories.map((cat) => ({
                value: cat,
                label: labels[cat],
              }))}
              className="w-full"
            />
          </div>

          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Description (optionnel)"
            rows={2}
            className={`${inputCls} resize-none`}
          />

          <input
            type="text"
            value={tagsInput}
            onChange={(e) => setTagsInput(e.target.value)}
            placeholder="Tags (separes par des virgules)"
            className={inputCls}
          />

          <div className="flex justify-between pt-1">
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              className="px-3 py-2 text-xs font-medium text-danger hover:bg-danger/10 rounded-md transition-colors"
            >
              Supprimer
            </button>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onClose}
                className="px-3 py-2 text-xs font-medium text-ink-muted border border-border/60 rounded-md hover:bg-surface-alt transition-colors"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleSave}
                className="px-3 py-2 text-xs font-medium bg-accent text-white rounded-md hover:opacity-80 transition-opacity"
              >
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={confirmDelete}
        title="Supprimer le creneau"
        message="Supprimer cet event recurrent et toutes ses occurrences ?"
        confirmLabel="Supprimer"
        variant="danger"
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(false)}
      />
    </>,
    document.body,
  );
}
